import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import {
  format,
  startOfWeek,
  eachDayOfInterval,
  eachMonthOfInterval,
  isWithinInterval,
  differenceInCalendarDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { Box } from '@mui/material';
import { PeriodType } from '../periodToggle';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface TagData {
  name: string;
  total: number;
  date: string;
}

interface TagBarProps {
  dataType: 'income' | 'expense';
  period: PeriodType;
  from?: string;
  to?: string;
}

const pct = (part: number, sum: number) => (sum > 0 ? Math.round((part / sum) * 100) : 0);

const hashToColor = (s: string, alpha = 0.6) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const r = 40 + (h % 160);
  const g = 40 + ((h >> 8) % 160);
  const b = 40 + ((h >> 16) % 160);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TagBar: React.FC<TagBarProps> = ({ dataType, period, from, to }) => {
  const endpoint = dataType === 'income' ? 'tag-income/stats' : 'tag-expense/stats';

  const queryParams = useMemo(() => {
    const params: Record<string, string> = { period };

    if (period === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }

    return params;
  }, [period, from, to]);

  const { data = [] } = useQuery<TagData[]>({
    queryKey: ['tag-bar', dataType, period, from, to],
    queryFn: () => api.get(`/${endpoint}`, { params: queryParams }).then(r => r.data),
  });

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const today = now.getDate();

  const computed = useMemo(() => {
    let labels: string[] = [];
    let slotsCount = 0;

    const weekStart = startOfWeek(now, { locale: ru, weekStartsOn: 1 });
    const weekDays = eachDayOfInterval({ start: weekStart, end: now });

    if (period === 'years') {
      labels = [String(currentYear - 1), String(currentYear)];
      slotsCount = labels.length;
    }

    if (period === 'year') {
      labels = Array.from({ length: currentMonth + 1 }, (_, m) =>
        format(new Date(currentYear, m, 1), 'LLLL', { locale: ru }),
      );
      slotsCount = labels.length;
    }

    if (period === 'month') {
      const weeks = Math.ceil(today / 7);
      slotsCount = weeks;

      labels = Array.from({ length: weeks }, (_, i) => {
        const start = i * 7 + 1;
        const end = Math.min((i + 1) * 7, today);
        return `${start}-${end} ${format(now, 'LLLL', { locale: ru })}`;
      });
    }

    if (period === 'week') {
      slotsCount = weekDays.length;
      labels = weekDays.map(d => format(d, 'EEEEEE', { locale: ru }));
    }

    if (period === 'custom' && from && to) {
      const start = new Date(from);
      const end = new Date(to);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
        return {
          chartData: { labels: [], datasets: [] } as ChartData<'bar'>,
          hasData: false,
          grandTotal: 0,
        };
      }

      end.setHours(23, 59, 59, 999);

      const totalDays = differenceInCalendarDays(end, start) + 1;

      if (totalDays <= 31) {
        const days = eachDayOfInterval({ start, end });
        labels = days.map(d => format(d, 'dd.MM', { locale: ru }));
        slotsCount = labels.length;

        const tagMap: Record<string, number[]> = {};
        const tagSum: Record<string, number> = {};

        data.forEach(d => {
          const dt = new Date(d.date);
          if (!isWithinInterval(dt, { start, end })) return;

          const idx = days.findIndex(x => x.toDateString() === dt.toDateString());
          if (idx < 0) return;

          if (!tagMap[d.name]) tagMap[d.name] = new Array(slotsCount).fill(0);
          tagMap[d.name][idx] += Number(d.total);
          tagSum[d.name] = (tagSum[d.name] ?? 0) + Number(d.total);
        });

        const tagsSorted = Object.entries(tagMap)
          .map(([name, arr]) => ({
            name,
            sum: tagSum[name] ?? arr.reduce((a, b) => a + b, 0),
            data: arr,
          }))
          .sort((a, b) => b.sum - a.sum);

        const grandTotal = tagsSorted.reduce((a, t) => a + t.sum, 0);

        const datasets = tagsSorted.map(t => ({
          label: `${t.name} (${pct(t.sum, grandTotal)}%)`,
          data: t.data,
          backgroundColor: hashToColor(t.name, 0.6),
          _tagSum: t.sum,
        })) as any[];

        const chartData: ChartData<'bar'> = { labels, datasets };
        const hasData =
          datasets.length > 0 &&
          datasets.some(ds => (ds.data as number[]).some((v: number) => v > 0));

        return { chartData, hasData, grandTotal };
      }

      if (totalDays <= 120) {
        const days = eachDayOfInterval({ start, end });
        const weekMap = new Map<string, number>();

        days.forEach(day => {
          const bucketStart = startOfWeek(day, { weekStartsOn: 1 });
          const key = format(bucketStart, 'dd.MM.yyyy');

          if (!weekMap.has(key)) {
            weekMap.set(key, weekMap.size);
            labels.push(`${format(bucketStart, 'dd.MM')} нед.`);
          }
        });

        slotsCount = labels.length;

        const tagMap: Record<string, number[]> = {};
        const tagSum: Record<string, number> = {};

        data.forEach(d => {
          const dt = new Date(d.date);
          if (!isWithinInterval(dt, { start, end })) return;

          const bucketStart = startOfWeek(dt, { weekStartsOn: 1 });
          const key = format(bucketStart, 'dd.MM.yyyy');
          const idx = weekMap.get(key);
          if (idx === undefined) return;

          if (!tagMap[d.name]) tagMap[d.name] = new Array(slotsCount).fill(0);
          tagMap[d.name][idx] += Number(d.total);
          tagSum[d.name] = (tagSum[d.name] ?? 0) + Number(d.total);
        });

        const tagsSorted = Object.entries(tagMap)
          .map(([name, arr]) => ({
            name,
            sum: tagSum[name] ?? arr.reduce((a, b) => a + b, 0),
            data: arr,
          }))
          .sort((a, b) => b.sum - a.sum);

        const grandTotal = tagsSorted.reduce((a, t) => a + t.sum, 0);

        const datasets = tagsSorted.map(t => ({
          label: `${t.name} (${pct(t.sum, grandTotal)}%)`,
          data: t.data,
          backgroundColor: hashToColor(t.name, 0.6),
          _tagSum: t.sum,
        })) as any[];

        const chartData: ChartData<'bar'> = { labels, datasets };
        const hasData =
          datasets.length > 0 &&
          datasets.some(ds => (ds.data as number[]).some((v: number) => v > 0));

        return { chartData, hasData, grandTotal };
      }

      const months = eachMonthOfInterval({ start, end });
      labels = months.map(m => format(m, 'LLLL yyyy', { locale: ru }));
      slotsCount = labels.length;

      const tagMap: Record<string, number[]> = {};
      const tagSum: Record<string, number> = {};

      data.forEach(d => {
        const dt = new Date(d.date);
        if (!isWithinInterval(dt, { start, end })) return;

        const idx = months.findIndex(
          m => m.getFullYear() === dt.getFullYear() && m.getMonth() === dt.getMonth(),
        );
        if (idx < 0) return;

        if (!tagMap[d.name]) tagMap[d.name] = new Array(slotsCount).fill(0);
        tagMap[d.name][idx] += Number(d.total);
        tagSum[d.name] = (tagSum[d.name] ?? 0) + Number(d.total);
      });

      const tagsSorted = Object.entries(tagMap)
        .map(([name, arr]) => ({
          name,
          sum: tagSum[name] ?? arr.reduce((a, b) => a + b, 0),
          data: arr,
        }))
        .sort((a, b) => b.sum - a.sum);

      const grandTotal = tagsSorted.reduce((a, t) => a + t.sum, 0);

      const datasets = tagsSorted.map(t => ({
        label: `${t.name} (${pct(t.sum, grandTotal)}%)`,
        data: t.data,
        backgroundColor: hashToColor(t.name, 0.6),
        _tagSum: t.sum,
      })) as any[];

      const chartData: ChartData<'bar'> = { labels, datasets };
      const hasData =
        datasets.length > 0 &&
        datasets.some(ds => (ds.data as number[]).some((v: number) => v > 0));

      return { chartData, hasData, grandTotal };
    }

    const tagMap: Record<string, number[]> = {};
    const tagSum: Record<string, number> = {};

    data.forEach(d => {
      const dt = new Date(d.date);
      let idx = -1;

      if (period === 'years') {
        const y = dt.getFullYear();
        if (y === currentYear - 1) idx = 0;
        else if (y === currentYear) idx = 1;
        else return;
      }

      if (period === 'year') {
        if (dt.getFullYear() !== currentYear) return;
        idx = dt.getMonth();
        if (idx > currentMonth) return;
      }

      if (period === 'month') {
        if (dt.getFullYear() !== currentYear) return;
        if (dt.getMonth() !== currentMonth) return;
        if (dt.getDate() > today) return;
        idx = Math.floor((dt.getDate() - 1) / 7);
      }

      if (period === 'week') {
        idx = weekDays.findIndex(x => x.toDateString() === dt.toDateString());
        if (idx < 0) return;
      }

      if (!tagMap[d.name]) tagMap[d.name] = new Array(slotsCount).fill(0);
      tagMap[d.name][idx] += Number(d.total);

      tagSum[d.name] = (tagSum[d.name] ?? 0) + Number(d.total);
    });

    const tagsSorted = Object.entries(tagMap)
      .map(([name, arr]) => ({
        name,
        sum: tagSum[name] ?? arr.reduce((a, b) => a + b, 0),
        data: arr,
      }))
      .sort((a, b) => b.sum - a.sum);

    const grandTotal = tagsSorted.reduce((a, t) => a + t.sum, 0);

    const datasets = tagsSorted.map(t => ({
      label: `${t.name} (${pct(t.sum, grandTotal)}%)`,
      data: t.data,
      backgroundColor: hashToColor(t.name, 0.6),
      _tagSum: t.sum,
    })) as any[];

    const chartData: ChartData<'bar'> = { labels, datasets };

    const hasData =
      datasets.length > 0 && datasets.some(ds => (ds.data as number[]).some((v: number) => v > 0));

    return { chartData, hasData, grandTotal };
  }, [data, period, now, currentYear, currentMonth, today, from, to]);

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: 'Статистика по тегам' },
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => {
              const v = Number(ctx.parsed.y || 0);
              const dsAny = ctx.dataset as any;
              const tagTotal = Number(dsAny._tagSum || 0);

              const p1 = pct(v, tagTotal);
              const p2 = pct(v, computed.grandTotal);

              return `${ctx.dataset.label}: ${v} ₽ (${p1}% от тега, ${p2}% от общего)`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text:
              period === 'years'
                ? 'Год'
                : period === 'year'
                  ? 'Месяц'
                  : period === 'month'
                    ? 'Неделя'
                    : period === 'week'
                      ? 'День недели'
                      : 'Выбранный период',
          },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Сумма, ₽' },
        },
      },
    }),
    [period, computed.grandTotal],
  );

  if (!computed.hasData) return <div>Нет записей для построения графики</div>;

  return (
    <Box sx={{ width: '100%', height: { xs: 320, sm: 420, md: 520 } }}>
      <Bar data={computed.chartData} options={options} />
    </Box>
  );
};

export default TagBar;
